import {
	type FanShopProductCategoryWithImages as FanShopProductCategory,
	type FanShopProductCategoryWithoutParent,
	useIntl
} from '@capsicum/common';
import { useCallback, useMemo } from 'react';

import { useCategoriesQueries } from '../data-access/queries/useCategoriesQueries';
import { type ParentCategories, type ParentCategory } from '../models';

export type ChildrenCategoryMemoProps = {
	id?: string;
	listIndex?: number;
	name?: string;
	subcategory: Array<FanShopProductCategory>;
};

export const useCategories = (partnerIds?: Array<string>) => {
	const { getLocalizedField } = useIntl();
	const partners = partnerIds ? partnerIds : [];

	const { data, isError, isLoading, refetch } = useCategoriesQueries(partners);

	const sortFn = useCallback(
		(a: FanShopProductCategory, b: FanShopProductCategory) => (a?.displayOrder || 0) - (b?.displayOrder || 0),
		[]
	);

	// Category by predicate
	const getCategoryBy = useCallback(
		(predicate: (p: FanShopProductCategory) => boolean) => data?.find(predicate),
		[data]
	);

	const parentCategories = useMemo(
		() =>
			data
				?.reduce((acc: ParentCategories, current: FanShopProductCategory) => {
					const currentParent = current?.parentCategory;
					const currentParentIndex = acc?.findIndex(parent => parent?.id === currentParent?.id);

					const currentWithTranslation = { ...current, name: getLocalizedField('name', current) };

					if (currentParentIndex === -1) {
						acc.push({
							...currentParent,
							children: [currentWithTranslation],
							name: getLocalizedField('name', currentParent!)
						});
					} else {
						acc[currentParentIndex]?.children.push(currentWithTranslation);
					}

					return acc;
				}, [])
				?.sort(sortFn)
				?.map(cat => ({
					...cat,
					children: cat?.children?.sort(sortFn)
				})),
		[data, sortFn]
	);

	const getCategoryImage = useCallback((category: FanShopProductCategory) => category?.images?.[0]?.url || '', []);
	const getCategoryBlurHash = useCallback(
		(category: FanShopProductCategory) => category?.images?.[0]?.blurHash || '',
		[]
	);

	const getSubcategoryBy = useCallback(
		(
			predicate: (subcategory: FanShopProductCategoryWithoutParent) => boolean
		): FanShopProductCategoryWithoutParent | undefined => data?.find(predicate),
		[data]
	);

	const getProductCategoriesBy = useCallback(
		(predicate: (category: FanShopProductCategory) => boolean): Array<FanShopProductCategory> =>
			data?.filter(predicate) || [],
		[data]
	);

	const getProductAllSubcategories = useCallback(
		() =>
			parentCategories?.reduce((acc: Array<FanShopProductCategory>, current: ParentCategory) => {
				if (current.children && current.children.length > 0) {
					acc = acc.concat(current.children);
				}

				return acc;
			}, []) || [],
		[parentCategories]
	);

	return {
		getCategoryBlurHash,
		getCategoryBy,
		getCategoryImage,
		getProductAllSubcategories,
		getProductCategoriesBy,
		getSubcategoryBy,
		isError,
		isLoading,
		parentCategories,
		refetch
	};
};
